import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, MessageSquare, Mic } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { useAudioProcessing } from '@/hooks/useAudioProcessing';
import { RolePlayButton } from '@/components/RolePlayButton';
import { RealtimeChat } from '@/utils/RealtimeAudio';
import { AudioQueue } from '@/utils/AudioPlayer';
import { ChatMessage } from '@/components/chat/ChatMessage';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

const AiRolePlay = () => {
  const [isListening, setIsListening] = useState(false);
  const [chatInstance, setChatInstance] = useState<RealtimeChat | null>(null);
  const [audioQueue, setAudioQueue] = useState<AudioQueue | null>(null);
  const [messages, setMessages] = useState<Message[]>([{
    role: 'assistant',
    content: "Hello! I'm Sarah, the property manager. How can I help you today?"
  }]);
  const { toast } = useToast();

  const handleMessage = (event: any) => {
    console.log('Received message:', event);
    if (event.type === 'response.audio.delta') {
      handleAudioResponse(event.delta);
    } else if (event.type === 'response.text.delta') {
      setMessages(prev => {
        const lastMessage = prev[prev.length - 1];
        if (lastMessage && lastMessage.role === 'assistant') {
          return [
            ...prev.slice(0, -1),
            { ...lastMessage, content: lastMessage.content + event.delta }
          ];
        } else {
          return [...prev, { role: 'assistant', content: event.delta }];
        }
      });
    }
  };

  const startRolePlaying = async () => {
    try {
      await initializeAudioContext();
      
      const newAudioQueue = new AudioQueue(audioContextRef.current!);
      setAudioQueue(newAudioQueue);
      
      const chat = new RealtimeChat(handleMessage);
      await chat.init();
      setChatInstance(chat);
      setIsListening(true);
      
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: "I can hear you now. What would you like to discuss?"
      }]);

      toast({
        title: "Connected",
        description: "AI Role Play session started. Start speaking!",
      });
    } catch (error) {
      console.error('Error starting role play:', error);
      toast({
        title: "Error",
        description: "Failed to start role play session. Please try again.",
        variant: "destructive"
      });
    }
  };

  const stopRolePlaying = () => {
    chatInstance?.disconnect();
    setChatInstance(null);
    setIsListening(false);
    audioQueue?.clear();
    
    setMessages(prev => [...prev, {
      role: 'assistant',
      content: "Thanks for the conversation. Feel free to start again when you're ready."
    }]);

    toast({
      title: "Session Ended",
      description: "AI Role Play session has ended.",
    });
  };

  const handleAudioResponse = async (audioBase64: string) => {
    if (!audioQueue) return;

    try {
      const binaryString = atob(audioBase64);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }

      await audioQueue.addToQueue(bytes);
    } catch (error) {
      console.error('Error processing audio response:', error);
    }
  };

  const { initializeAudioContext, audioContextRef } = useAudioProcessing((audioData) => {
    if (isListening && chatInstance) {
      chatInstance.sendMessage({
        type: 'input_audio_buffer.append',
        audio: audioData
      });
      
      // Add user message when speaking starts
      setMessages(prev => {
        if (prev[prev.length - 1]?.role === 'user') return prev;
        return [...prev, { role: 'user', content: '🎤 Speaking...' }];
      });
    }
  });

  useEffect(() => {
    return () => {
      chatInstance?.disconnect();
      audioQueue?.clear();
    };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white">
      <div className="container mx-auto px-4 py-8">
        <div className="mb-6 flex items-center gap-4">
          <Link to="/">
            <Button variant="ghost" className="flex items-center gap-2">
              <ArrowLeft className="h-4 w-4" />
              Back
            </Button>
          </Link>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <MessageSquare className="h-6 w-6 text-purple-500" />
            Property Manager Role Play
          </h1>
        </div>

        <div className="max-w-4xl mx-auto">
          <div className="mb-8 flex items-center gap-4">
            <Avatar className="h-12 w-12">
              <AvatarImage src="https://i.pravatar.cc/100?img=47" alt="Sarah" />
              <AvatarFallback>SW</AvatarFallback>
            </Avatar>
            <div>
              <h2 className="text-lg font-semibold">Sarah Williams</h2>
              <p className="text-sm text-gray-500">Commercial Property Manager</p>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
            <div className="space-y-4 mb-8 max-h-[400px] overflow-y-auto">
              {messages.map((message, index) => (
                <ChatMessage key={index} {...message} />
              ))}
            </div>

            <RolePlayButton 
              isListening={isListening}
              onClick={() => isListening ? stopRolePlaying() : startRolePlaying()}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AiRolePlay;
